import React, { useState } from "react";
import SectionShell from "./SectionShell";
import { getSidebarByRole } from "../../../Data/Data";

import { SIDEBAR_CONFIG } from "../../../Data/Data";

const AVAILABLE_ROLES = ["admin", "superAdmin", "waiter", "fingliderAdmin"];

const SidebarPermissionsSection = ({ permissions, isLoading, onSave }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState(permissions || {});
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    const init = { ...(permissions || {}) };
  
    SIDEBAR_CONFIG.forEach(menu => {
      if (init[menu.id] === undefined) {
        init[menu.id] = menu.roles || AVAILABLE_ROLES;
      }
      if (menu.submenu) {
        menu.submenu.forEach(sub => {
          if (init[sub.id] === undefined) {
            init[sub.id] = sub.roles || menu.roles || AVAILABLE_ROLES;
          }
        });
      }
    });

    setEditedPermissions(init);
  }, [permissions]);

  const handleToggleRole = (menuId, role) => {
    setEditedPermissions((prev) => {
      const currentRoles = prev[menuId] || [];
      let newRoles;
      if (currentRoles.includes(role)) {
        newRoles = currentRoles.filter((r) => r !== role);
      } else {
        newRoles = [...currentRoles, role];
      }
      return { ...prev, [menuId]: newRoles };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(editedPermissions);
    setIsSaving(false);
  };

  return (
    <SectionShell
      number="5"
      title="Role Permissions"
      subtitle="Manage sidebar menu visibility for different user roles"
    >
      {isLoading ? (
        <div className="flex justify-center p-8">
          <div className="text-sm text-gray-500">Loading permissions...</div>
        </div>
      ) : (
        <>
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {SIDEBAR_CONFIG.map((menu) => (
              <React.Fragment key={menu.id}>
                {/* Main Menu Item */}
                <div className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="font-semibold text-gray-800 mb-3 md:mb-0">
                    {menu.heading}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {AVAILABLE_ROLES.map((role) => {
                      const hasAccess =
                        (editedPermissions[menu.id] || []).includes(role);
                      return (
                        <label
                          key={`${menu.id}-${role}`}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={hasAccess}
                            onChange={() => handleToggleRole(menu.id, role)}
                            className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-600 capitalize">
                            {role.replace("Admin", " Admin")}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Submenu Items */}
                {menu.submenu &&
                  menu.submenu.map((sub) => (
                    <div
                      key={sub.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-3 ml-8 bg-white rounded-lg border border-gray-100"
                    >
                      <div className="font-medium text-gray-600 mb-2 md:mb-0 flex items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mr-2"></div>
                        {sub.heading}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {AVAILABLE_ROLES.map((role) => {
                          const hasAccess =
                            (editedPermissions[sub.id] || []).includes(role);
                          return (
                            <label
                              key={`${sub.id}-${role}`}
                              className="flex items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={hasAccess}
                                onChange={() => handleToggleRole(sub.id, role)}
                                className="w-4 h-4 text-emerald-500 rounded border-gray-300 focus:ring-emerald-400"
                              />
                              <span className="text-sm text-gray-500 capitalize">
                                {role.replace("Admin", " Admin")}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </React.Fragment>
            ))}
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full md:w-auto px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 active:scale-[0.98] transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <span>{isSaving ? "Saving..." : "Save Permissions"}</span>
            </button>
          </div>
        </>
      )}
    </SectionShell>
  );
};

export default SidebarPermissionsSection;
